import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const bannersAdapter = createEntityAdapter({
    selectId: (banner) => banner._id,
});

// Async Thunks
export const fetchBanners = createAsyncThunk(
    "banners/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/getAllBanners",
                method: "GET",
            });

            if (!response.data || !Array.isArray(response.data)) {
                return rejectWithValue(response.error?.message || "Failed to fetch banners.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error?.message || "An unexpected error occurred while fetching banners.");
        }
    }
);

export const addBanner = createAsyncThunk(
    "banners/add",
    async ({ title, subtitle, image }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("title", title);
            formData.append("subtitle", subtitle);
            formData.append("image", image);

            const response = await request({
                endpoint: "/admin/addBanner",
                method: "POST",
                body: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (!response.success || !response.data) {
                return rejectWithValue(response.error?.message || "Failed to add banner.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error?.message || "An unexpected error occurred while adding the banner.");
        }
    }
);

export const updateBanner = createAsyncThunk(
    "banners/update",
    async ({ id, title, subtitle, image }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("title", title);
            formData.append("subtitle", subtitle);
            if (image) formData.append("image", image);

            const response = await request({
                endpoint: "/admin/updateBanner",
                method: "PUT",
                body: formData,
                headers: { "Content-Type": "multipart/form-data" },
                params: { id },
            });

            if (!response.success || !response.data) {
                return rejectWithValue(response.error?.message || "Failed to update banner.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error?.message || "An unexpected error occurred while updating the banner.");
        }
    }
);

export const removeBanner = createAsyncThunk(
    "banners/remove",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/removeBanner",
                method: "DELETE",
                params: { id },
            });

            if (!response.success) {
                return rejectWithValue(response.error?.message || "Failed to remove banner.");
            }

            return id;
        } catch ({ error }) {
            return rejectWithValue(error?.message || "An unexpected error occurred while removing the banner.");
        }
    }
);

// Initial state
const initialState = bannersAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const bannerSlice = createSlice({
    name: "banners",
    initialState,
    reducers: {
        clearBanners: (state) => {
            bannersAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBanners.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBanners.fulfilled, (state, action) => {
                bannersAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchBanners.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(addBanner.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addBanner.fulfilled, (state, action) => {
                bannersAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(addBanner.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateBanner.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateBanner.fulfilled, (state, action) => {
                bannersAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(updateBanner.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(removeBanner.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeBanner.fulfilled, (state, action) => {
                bannersAdapter.removeOne(state, action.payload);
                state.loading = false;
            })
            .addCase(removeBanner.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Selectors
export const {
    selectAll: selectAllBanners,
    selectById: selectBannerById,
    selectIds: selectBannerIds,
} = bannersAdapter.getSelectors((state) => state.banners);

// Actions & Reducer Export
export const { clearBanners } = bannerSlice.actions;
export default bannerSlice.reducer;
